/** Suppliers, purchase orders, goods receipts, returns and payables (docs/specs/purchasing.md). */
export const purchasing = {
    total: "Total",
    lineTotal: "Line total",
    product: "Product",
    quantity: "Quantity",
    unitCost: "Unit cost",
    note: "Note",
    supplier: "Supplier",
    location: "Location",
    date: "Date",
    all: "All",
    noLines: "Add at least one product.",
    suppliers: {
        title: "Suppliers",
        description: "Who you buy from, and what you owe each of them.",
        new: "New supplier",
        search: "Search name or phone",
        columns: { name: "Name", contact: "Contact", phone: "Phone", balance: "You owe", status: "Status" },
        statuses: { active: "Active", archived: "Archived" },
        empty: "No suppliers yet.",
        back: "All suppliers",
        balance: "You owe",
        advance: "Paid in advance",
        opening: "Opening balance",
        form: {
            titleNew: "New supplier",
            titleEdit: "Edit supplier",
            name: "Name",
            contactName: "Contact person",
            phone: "Mobile number",
            email: "Email",
            address: "Address",
            note: "Note",
            openingBalance: "Already owed (opening balance)",
            openingBalanceHint: "What you owed this supplier before using the system. Set once.",
            save: "Save supplier",
        },
        created: "Supplier added.",
        saved: "Supplier saved.",
        edit: "Edit",
        archive: "Archive",
        unarchive: "Unarchive",
        archived: "Supplier archived.",
        unarchived: "Supplier active again.",
        conflict: "Someone else changed this supplier. The latest version is shown; try again.",
        receipts: "Goods received",
        returns: "Returns",
        payments: "Payments",
    },
    payments: {
        title: "Record payment",
        record: "Record payment",
        amount: "Amount",
        method: "Method",
        reference: "Reference (TrxID, cheque no.)",
        paidAt: "Paid on",
        save: "Save payment",
        recorded: "Payment recorded.",
        reverse: "Reverse",
        reverseTitle: "Reverse this payment?",
        reverseBody: "A reversing entry is added; the payment stays in the history.",
        reverseNote: "Why?",
        reversed: "Payment reversed.",
        reversal: "Reversal",
        reversedLabel: "Reversed",
        empty: "No payments yet.",
        columns: { number: "Number", date: "Date", amount: "Amount", method: "Method", reference: "Reference" },
        methods: {
            cash: "Cash",
            bank_transfer: "Bank transfer",
            cheque: "Cheque",
            bkash: "bKash",
            nagad: "Nagad",
            rocket: "Rocket",
            other: "Other",
        },
    },
    orders: {
        title: "Purchase orders",
        description: "What you ordered from suppliers and what has arrived.",
        new: "New purchase order",
        columns: {
            number: "Number",
            supplier: "Supplier",
            location: "Deliver to",
            status: "Status",
            total: "Total",
            date: "Created",
        },
        statuses: {
            draft: "Draft",
            sent: "Sent",
            partially_received: "Partly received",
            received: "Received",
            closed: "Closed",
            cancelled: "Cancelled",
        },
        empty: "No purchase orders yet.",
        back: "All purchase orders",
        expected: "Expected by",
        steps: { sent: "Sent", closed: "Closed", cancelled: "Cancelled" },
        actions: {
            send: "Mark as sent",
            close: "Close order",
            cancel: "Cancel order",
            pdf: "Download PDF",
            receive: "Receive goods",
        },
        done: {
            created: "Purchase order created.",
            send: "Marked as sent.",
            close: "Purchase order closed.",
            cancel: "Purchase order cancelled.",
        },
        cancelTitle: "Cancel this purchase order?",
        cancelBody: "Nothing has arrived yet; the order stays in the list as cancelled.",
        closeTitle: "Close this purchase order?",
        closeBody: "What has not arrived yet is no longer expected.",
        conflict: "Someone else changed this purchase order. The latest version is shown; try again.",
        form: {
            title: "New purchase order",
            description: "Pick the supplier, where the goods go, then the products and their cost.",
            expectedDate: "Expected by (optional)",
            submit: "Create draft",
        },
        lines: { title: "Products", ordered: "Ordered", received: "Received" },
    },
    receipts: {
        title: "Goods received",
        description: "Every delivery booked into stock, with or without a purchase order.",
        new: "Receive without order",
        columns: {
            number: "Number",
            supplier: "Supplier",
            location: "Location",
            invoice: "Invoice",
            total: "Total",
            date: "Received",
        },
        empty: "No goods received yet.",
        back: "All goods received",
        order: "Purchase order",
        form: {
            title: "Receive goods",
            titleForOrder: "Receive goods for {{number}}",
            description: "Quantities go into stock at the cost you enter; it updates the average cost.",
            invoiceRef: "Supplier invoice no.",
            receivedAt: "Received on",
            left: "Still open: {{qty}}",
            submit: "Book into stock",
        },
        done: "Goods booked into stock.",
        returned: "Returned",
        returnGoods: "Return goods",
    },
    returns: {
        title: "Returns to suppliers",
        description: "Goods sent back; each return lowers what you owe.",
        new: "New return",
        columns: {
            number: "Number",
            supplier: "Supplier",
            location: "Location",
            reason: "Reason",
            total: "Total",
            date: "Date",
        },
        empty: "No returns yet.",
        back: "All returns",
        receipt: "Goods receipt",
        form: {
            title: "Return goods",
            titleForReceipt: "Return goods from {{number}}",
            description: "Stock goes down; the supplier owes you the value.",
            reason: "Reason",
            costHint: "Empty: the receipt's cost, or the current average cost.",
            left: "Can return: {{qty}}",
            submit: "Return goods",
        },
        done: "Return booked.",
        reasons: {
            damaged: "Damaged",
            wrong_item: "Wrong item",
            expired: "Expired",
            excess: "Too many",
            quality: "Quality problem",
            other: "Other",
        },
    },
    aging: {
        title: "Payables",
        description: "What you owe each supplier, by how old the deliveries are. Payments settle the oldest first.",
        columns: {
            supplier: "Supplier",
            balance: "You owe",
            current: "0–30 days",
            days31to60: "31–60 days",
            days61to90: "61–90 days",
            over90: "Over 90 days",
            advance: "Advance",
        },
        totals: "Total",
        empty: "You owe no supplier anything.",
    },
};
//# sourceMappingURL=purchasing.js.map